import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Wrench, Truck, ChevronDown, ChevronUp, Plus, History, Pencil, Trash2, X,
  ArrowUpToLine, ArrowDownToLine, Loader2, AlertTriangle, CheckCircle2, Wrench as WrenchIcon, FileText,
} from "lucide-react";

const CONDIZIONI = [
  { value: "buone", label: "Buone", color: "#16A34A", bg: "#DCFCE7" },
  { value: "danneggiate", label: "Danneggiate", color: "#DC2626", bg: "#FEE2E2" },
  { value: "da_revisionare", label: "Da revisionare", color: "#D97706", bg: "#FEF3C7" },
];

function CondizionePill({ value }) {
  const c = CONDIZIONI.find((x) => x.value === value) || CONDIZIONI[0];
  return (
    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide" style={{ background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function openVerbalePdf(movId) {
  // Open the PDF in a new tab using current auth cookie/header.
  // We use api.get with responseType=blob then create a temporary URL,
  // so the JWT bearer token in headers is honored.
  api.get(`/dotazione/movimenti/${movId}/verbale.pdf`, { responseType: "blob" })
    .then((r) => {
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    })
    .catch((e) => toast.error(formatApiError(e)));
}

// ---------- Form Asset (attrezzatura o mezzo) ----------
function AssetFormModal({ kind, asset, onClose, onSaved }) {
  const isEdit = !!asset?.id;
  const isAtt = kind === "attrezzatura";
  const [form, setForm] = useState(asset || (isAtt
    ? { nome: "", codice: "", descrizione: "", has_id: false, quantita_totale: 1, tipo: "", note: "" }
    : { targa: "", modello: "", marca: "", anno: "", tipo: "", note: "" }));
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isAtt ? "/attrezzature" : "/mezzi";
      const payload = { ...form };
      if (!isAtt && payload.anno === "") delete payload.anno;
      if (!isAtt && payload.anno) payload.anno = Number(payload.anno);
      if (isAtt) payload.quantita_totale = Number(payload.quantita_totale) || 1;
      if (isEdit) await api.put(`${url}/${asset.id}`, payload);
      else await api.post(url, payload);
      toast.success(isEdit ? "Aggiornato" : "Creato");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="card-shell w-full max-w-lg my-auto" onClick={(e) => e.stopPropagation()} data-testid={`${kind}-form-modal`}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>
            {isEdit ? "Modifica" : "Nuovo"} {isAtt ? "attrezzatura" : "mezzo"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          {isAtt ? (
            <>
              <Field label="Nome *">
                <input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="form-input" data-testid="attr-nome" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Codice/Matricola">
                  <input value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })}
                    className="form-input" data-testid="attr-codice" />
                </Field>
                <Field label="Tipo">
                  <input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    placeholder="es. trapano, casco…" className="form-input" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <label className="flex items-center gap-2 text-xs font-semibold">
                  <input type="checkbox" checked={form.has_id} onChange={(e) => setForm({ ...form, has_id: e.target.checked })} data-testid="attr-hasid" />
                  Pezzo unico identificabile
                </label>
                {!form.has_id && (
                  <Field label="Quantità totale">
                    <input type="number" min={1} value={form.quantita_totale}
                      onChange={(e) => setForm({ ...form, quantita_totale: e.target.value })} className="form-input" />
                  </Field>
                )}
              </div>
              <Field label="Descrizione / Note">
                <textarea rows={2} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="form-input" />
              </Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Targa *">
                  <input required value={form.targa} onChange={(e) => setForm({ ...form, targa: e.target.value.toUpperCase() })}
                    className="form-input uppercase" data-testid="mezzo-targa" />
                </Field>
                <Field label="Modello *">
                  <input required value={form.modello} onChange={(e) => setForm({ ...form, modello: e.target.value })}
                    className="form-input" data-testid="mezzo-modello" />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Marca">
                  <input value={form.marca} onChange={(e) => setForm({ ...form, marca: e.target.value })} className="form-input" />
                </Field>
                <Field label="Anno">
                  <input type="number" value={form.anno || ""} onChange={(e) => setForm({ ...form, anno: e.target.value })} className="form-input" />
                </Field>
                <Field label="Tipo">
                  <input value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                    placeholder="furgone, auto…" className="form-input" />
                </Field>
              </div>
              <Field label="Note">
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="form-input" />
              </Field>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Annulla</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="submit-asset">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ---------- Modal: consegna/restituzione ----------
function MovimentoModal({ asset, kind, mode, squadraTarget, onClose, onSaved }) {
  // mode: "consegna" | "restituzione"
  const [form, setForm] = useState({
    squadra_id: squadraTarget?.id || "",
    condizioni: "buone",
    quantita: 1,
    ricevuto_da_name: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);
  const label = kind === "attrezzatura" ? "attrezzatura" : "mezzo";
  const title = mode === "consegna" ? `Consegna ${label} alla squadra` : `Restituisci ${label}`;
  const assetLabel = kind === "attrezzatura" ? asset?.nome : `${asset?.targa} · ${asset?.modello}`;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = `/${kind === "attrezzatura" ? "attrezzature" : "mezzi"}/${asset.id}/${mode}`;
      const payload = mode === "consegna"
        ? { squadra_id: form.squadra_id, condizioni: form.condizioni, quantita: Number(form.quantita) || 1, ricevuto_da_name: form.ricevuto_da_name, note: form.note }
        : { condizioni: form.condizioni, quantita: Number(form.quantita) || 1, ricevuto_da_name: form.ricevuto_da_name, note: form.note };
      const resp = await api.post(url, payload);
      const movId = resp?.data?.movimento?.id;
      toast.success(mode === "consegna" ? "Consegnato" : "Restituito", movId ? {
        description: "Scarica il verbale firmabile",
        duration: 8000,
        action: { label: "Verbale PDF 📄", onClick: () => openVerbalePdf(movId) },
      } : undefined);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="card-shell w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={`${mode}-modal`}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="font-bold capitalize" style={{ fontFamily: "Manrope" }}>{title}</h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{assetLabel}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3 text-sm">
          {mode === "consegna" && squadraTarget && (
            <div className="p-3 rounded-lg bg-gray-50 border" style={{ borderColor: "var(--border)" }}>
              Squadra destinataria: <b style={{ color: squadraTarget.color }}>{squadraTarget.name}</b>
            </div>
          )}
          <Field label="Condizioni">
            <select value={form.condizioni} onChange={(e) => setForm({ ...form, condizioni: e.target.value })}
              className="form-input" data-testid="mov-condizioni">
              {CONDIZIONI.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          {kind === "attrezzatura" && !asset?.has_id && (
            <Field label="Quantità">
              <input type="number" min={1} value={form.quantita} onChange={(e) => setForm({ ...form, quantita: e.target.value })} className="form-input" />
            </Field>
          )}
          <Field label={mode === "consegna" ? "Ricevuto da (nome)" : "Riconsegnato da (nome)"}>
            <input value={form.ricevuto_da_name} onChange={(e) => setForm({ ...form, ricevuto_da_name: e.target.value })}
              placeholder="es. Mario Rossi" className="form-input" data-testid="mov-ricevuto-da" />
          </Field>
          <Field label="Note">
            <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="form-input" />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Annulla</button>
            <button type="submit" className="btn-primary" disabled={saving} data-testid="submit-movimento">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "consegna" ? <ArrowDownToLine className="w-4 h-4" /> : <ArrowUpToLine className="w-4 h-4" />)}
              {mode === "consegna" ? "Consegna" : "Restituisci"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------- Cronologia ----------
function CronologiaPanel({ squadra, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ asset_type: "all", tipo: "all" });

  useEffect(() => {
    setLoading(true);
    const params = { squadra_id: squadra.id, limit: 100 };
    if (filter.asset_type !== "all") params.asset_type = filter.asset_type;
    if (filter.tipo !== "all") params.tipo = filter.tipo;
    api.get("/dotazione/movimenti", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [squadra.id, filter.asset_type, filter.tipo]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="card-shell w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="cronologia-modal">
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="font-bold flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
              <History className="w-4 h-4" /> Cronologia · {squadra.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>Consegne e restituzioni</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b flex flex-wrap items-center gap-2" style={{ borderColor: "var(--border)" }}>
          <select value={filter.asset_type} onChange={(e) => setFilter({ ...filter, asset_type: e.target.value })}
            className="px-2 py-1 text-xs rounded border bg-white" style={{ borderColor: "var(--border)" }}>
            <option value="all">Tutti gli asset</option>
            <option value="attrezzatura">Solo attrezzature</option>
            <option value="mezzo">Solo mezzi</option>
          </select>
          <select value={filter.tipo} onChange={(e) => setFilter({ ...filter, tipo: e.target.value })}
            className="px-2 py-1 text-xs rounded border bg-white" style={{ borderColor: "var(--border)" }}>
            <option value="all">Tutti i movimenti</option>
            <option value="consegna">Solo consegne</option>
            <option value="restituzione">Solo restituzioni</option>
          </select>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              Nessun movimento registrato per questa squadra.
            </div>
          )}
          {!loading && items.map((m) => (
            <div key={m.id} className="px-4 py-3 border-b text-sm" style={{ borderColor: "var(--border)" }} data-testid={`mov-${m.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  {m.tipo === "consegna"
                    ? <ArrowDownToLine className="w-4 h-4" style={{ color: "#16A34A" }} />
                    : <ArrowUpToLine className="w-4 h-4" style={{ color: "#DC2626" }} />}
                  <b>{m.tipo === "consegna" ? "Consegna" : "Restituzione"}</b>
                  {m.asset_type === "attrezzatura"
                    ? <Wrench className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                    : <Truck className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />}
                  <span className="font-semibold">{m.asset_label}</span>
                </div>
                <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{fmtDate(m.timestamp)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs" style={{ color: "var(--text-muted)" }}>
                <CondizionePill value={m.condizioni} />
                {m.consegnato_da_name && <span>operatore: <b>{m.consegnato_da_name}</b></span>}
                {m.ricevuto_da_name && <span>{m.tipo === "consegna" ? "ricevuto da" : "riconsegnato da"}: <b>{m.ricevuto_da_name}</b></span>}
                {m.quantita > 1 && <span>q.tà: <b>{m.quantita}</b></span>}
              </div>
              {m.note && <div className="text-xs mt-1 italic" style={{ color: "#374151" }}>“{m.note}”</div>}
              <div className="mt-1 flex justify-end">
                <button
                  onClick={() => openVerbalePdf(m.id)}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded border hover:bg-black/5"
                  style={{ borderColor: "var(--border)" }}
                  data-testid={`btn-verbale-${m.id}`}
                >
                  <FileText className="w-3 h-3" /> Scarica verbale PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Sezione principale ----------
export default function DotazioneSection({ squadra }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ attrezzature: [], mezzi: [], count_attrezzature: 0, count_mezzi: 0 });
  const [loading, setLoading] = useState(false);
  const [formModal, setFormModal] = useState(null);          // {kind, asset?}
  const [movModal, setMovModal] = useState(null);            // {kind, asset, mode}
  const [showCronologia, setShowCronologia] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/squadre/${squadra.id}/dotazione`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [squadra.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleDelete = async (kind, asset) => {
    if (!window.confirm(`Eliminare ${kind === "attrezzatura" ? "questa attrezzatura" : "questo mezzo"}? L'azione è irreversibile.`)) return;
    try {
      await api.delete(`/${kind === "attrezzatura" ? "attrezzature" : "mezzi"}/${asset.id}`);
      toast.success("Eliminato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const totalCount = data.count_attrezzature + data.count_mezzi;

  return (
    <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid={`dotazione-section-${squadra.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-2 rounded-md hover:bg-black/5 transition"
        data-testid={`dotazione-toggle-${squadra.id}`}
      >
        <div className="flex items-center gap-2">
          <WrenchIcon className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
            Attrezzature & Mezzi
          </span>
          {totalCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "var(--brand-green)", color: "white" }}>
              {totalCount}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-2 space-y-3">
          {loading && <div className="text-center text-xs py-4"><Loader2 className="w-4 h-4 inline animate-spin" /></div>}

          {/* MEZZI */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-primary)" }}>
                <Truck className="w-3.5 h-3.5" /> Mezzi ({data.count_mezzi})
              </div>
              <button
                onClick={() => setFormModal({ kind: "mezzo" })}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md border hover:bg-black/5"
                style={{ borderColor: "var(--border)" }}
                data-testid={`btn-new-mezzo-${squadra.id}`}
              >
                <Plus className="w-3 h-3" /> Nuovo
              </button>
            </div>
            {data.mezzi.length === 0 ? (
              <div className="text-[11px] text-center py-2 italic" style={{ color: "var(--text-muted)" }}>Nessun mezzo assegnato</div>
            ) : (
              <div className="space-y-1">
                {data.mezzi.map((m) => (
                  <AssetRow key={m.id} asset={m} kind="mezzo"
                    label={`${m.targa} · ${m.modello}${m.marca ? " (" + m.marca + ")" : ""}`}
                    onReturn={() => setMovModal({ kind: "mezzo", asset: m, mode: "restituzione" })}
                    onEdit={() => setFormModal({ kind: "mezzo", asset: m })}
                    onDelete={() => handleDelete("mezzo", m)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ATTREZZATURE */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide" style={{ color: "var(--text-primary)" }}>
                <Wrench className="w-3.5 h-3.5" /> Attrezzature ({data.count_attrezzature})
              </div>
              <button
                onClick={() => setFormModal({ kind: "attrezzatura" })}
                className="text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md border hover:bg-black/5"
                style={{ borderColor: "var(--border)" }}
                data-testid={`btn-new-attr-${squadra.id}`}
              >
                <Plus className="w-3 h-3" /> Nuova
              </button>
            </div>
            {data.attrezzature.length === 0 ? (
              <div className="text-[11px] text-center py-2 italic" style={{ color: "var(--text-muted)" }}>Nessuna attrezzatura</div>
            ) : (
              <div className="space-y-1">
                {data.attrezzature.map((a) => (
                  <AssetRow key={a.id} asset={a} kind="attrezzatura"
                    label={`${a.nome}${a.codice ? " · " + a.codice : ""}${!a.has_id ? " × " + a.quantita_totale : ""}`}
                    onReturn={() => setMovModal({ kind: "attrezzatura", asset: a, mode: "restituzione" })}
                    onEdit={() => setFormModal({ kind: "attrezzatura", asset: a })}
                    onDelete={() => handleDelete("attrezzatura", a)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer: Consegna (asset da magazzino) + Cronologia */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <button
              onClick={() => setMovModal({ kind: "_pick_consegna_attrezzatura" })}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-black/5"
              style={{ borderColor: "var(--border)" }}
              data-testid={`btn-assign-attr-${squadra.id}`}
            >
              <ArrowDownToLine className="w-3 h-3" /> Consegna attrezzatura dal magazzino
            </button>
            <button
              onClick={() => setMovModal({ kind: "_pick_consegna_mezzo" })}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-black/5"
              style={{ borderColor: "var(--border)" }}
              data-testid={`btn-assign-mezzo-${squadra.id}`}
            >
              <ArrowDownToLine className="w-3 h-3" /> Consegna mezzo dal deposito
            </button>
            <button
              onClick={() => setShowCronologia(true)}
              className="ml-auto text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border hover:bg-black/5"
              style={{ borderColor: "var(--border)" }}
              data-testid={`btn-cronologia-${squadra.id}`}
            >
              <History className="w-3 h-3" /> Cronologia
            </button>
          </div>
        </div>
      )}

      {formModal && (
        <AssetFormModal
          kind={formModal.kind} asset={formModal.asset}
          onClose={() => setFormModal(null)}
          onSaved={() => { setFormModal(null); load(); }}
        />
      )}

      {movModal && (movModal.kind === "attrezzatura" || movModal.kind === "mezzo") && (
        <MovimentoModal
          kind={movModal.kind} asset={movModal.asset} mode={movModal.mode}
          squadraTarget={squadra}
          onClose={() => setMovModal(null)}
          onSaved={() => { setMovModal(null); load(); }}
        />
      )}

      {movModal && (movModal.kind === "_pick_consegna_attrezzatura" || movModal.kind === "_pick_consegna_mezzo") && (
        <PickAndAssignModal
          kind={movModal.kind === "_pick_consegna_attrezzatura" ? "attrezzatura" : "mezzo"}
          squadra={squadra}
          onClose={() => setMovModal(null)}
          onSaved={() => { setMovModal(null); load(); }}
        />
      )}

      {showCronologia && (
        <CronologiaPanel squadra={squadra} onClose={() => setShowCronologia(false)} />
      )}
    </div>
  );
}

function AssetRow({ asset, kind, label, onReturn, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50 text-xs" data-testid={`asset-row-${asset.id}`}>
      {kind === "mezzo" ? <Truck className="w-3.5 h-3.5 text-gray-500" /> : <Wrench className="w-3.5 h-3.5 text-gray-500" />}
      <span className="font-mono font-semibold flex-1 truncate" title={label}>{label}</span>
      <CondizionePill value={asset.condizioni_attuali || "buone"} />
      <button onClick={onReturn} className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white hover:brightness-110" style={{ background: "#DC2626" }} title="Restituisci" data-testid={`btn-return-${asset.id}`}>
        <ArrowUpToLine className="w-3 h-3" />
      </button>
      <button onClick={onEdit} className="p-1 rounded hover:bg-black/5" title="Modifica" data-testid={`btn-edit-${asset.id}`}>
        <Pencil className="w-3 h-3" />
      </button>
      <button onClick={onDelete} className="p-1 rounded hover:bg-black/5" title="Elimina" data-testid={`btn-del-${asset.id}`}>
        <Trash2 className="w-3 h-3" style={{ color: "#991B1B" }} />
      </button>
    </div>
  );
}

// Picker per assegnare un asset esistente non assegnato
function PickAndAssignModal({ kind, squadra, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [condizioni, setCondizioni] = useState("buone");
  const [note, setNote] = useState("");
  const url = kind === "attrezzatura" ? "/attrezzature" : "/mezzi";

  useEffect(() => {
    api.get(url, { params: { squadra_id: "none" } })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [url]);

  const assign = async (asset) => {
    setAssigning(asset.id);
    try {
      const r = await api.post(`${url}/${asset.id}/consegna`, {
        squadra_id: squadra.id, condizioni, note, quantita: 1,
      });
      const movId = r?.data?.movimento?.id;
      toast.success("Consegnato", movId ? {
        description: "Scarica il verbale firmabile",
        duration: 8000,
        action: { label: "Verbale PDF 📄", onClick: () => openVerbalePdf(movId) },
      } : undefined);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="card-shell w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid={`pick-assign-${kind}-modal`}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div>
            <h3 className="font-bold capitalize" style={{ fontFamily: "Manrope" }}>Consegna a {squadra.name}</h3>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              Scegli un{kind === "attrezzatura" ? "'attrezzatura" : " mezzo"} dal {kind === "attrezzatura" ? "magazzino" : "deposito"}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b grid grid-cols-2 gap-2" style={{ borderColor: "var(--border)" }}>
          <Field label="Condizioni alla consegna">
            <select value={condizioni} onChange={(e) => setCondizioni(e.target.value)} className="form-input">
              {CONDIZIONI.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Note">
            <input value={note} onChange={(e) => setNote(e.target.value)} className="form-input" />
          </Field>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }}>
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 opacity-50" />
              Nessun {kind === "attrezzatura" ? "attrezzo" : "mezzo"} disponibile in {kind === "attrezzatura" ? "magazzino" : "deposito"}.<br />
              Crea un nuovo elemento dal pulsante "Nuovo" sopra.
            </div>
          )}
          {!loading && items.map((it) => (
            <div key={it.id} className="px-4 py-2 border-b flex items-center gap-2 hover:bg-gray-50" style={{ borderColor: "var(--border)" }} data-testid={`assignable-${it.id}`}>
              <span className="flex-1 text-sm font-mono">
                {kind === "attrezzatura"
                  ? `${it.nome}${it.codice ? " · " + it.codice : ""}`
                  : `${it.targa} · ${it.modello}`}
              </span>
              <button
                onClick={() => assign(it)}
                disabled={assigning === it.id}
                className="btn-primary text-xs" style={{ padding: "4px 10px" }}
                data-testid={`assign-${it.id}`}
              >
                {assigning === it.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowDownToLine className="w-3 h-3" />}
                Consegna
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
